import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { X } from 'react-feather'
import { useTranslation } from 'react-i18next'
import Modal from '../../components/Modal'
import { ButtonPrimary, ButtonSecondary } from '../../components/Button'
import { Pool } from './types'
import ROICalculatorModal from './ROICalculatorModal'

const StakeModalContent = styled.div`
  padding: 2rem;
  background: linear-gradient(to bottom, #000000, #231f20);
  color: #ffffff;
  width: 100%;
  box-sizing: border-box;
`

const StakeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`

const StakeTitle = styled.h2`
  color: #ffffff;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
`

const StakeCloseButton = styled(X)`
  cursor: pointer;
  color: #ffffff;
  width: 24px;
  height: 24px;
  border: 1px solid #565656;
  border-radius: 50%;
  padding: 0.25rem;

  &:hover {
    color: #e9ad21;
  }
`

const Section = styled.div`
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
`

const StakeHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`

const StakeSectionLabel = styled.div`
  color: #e9ad21;
  font-size: 0.9rem;
  font-weight: 700;
`

const StakeAmountCard = styled.div`
  border-radius: 16px;
  border: 1px solid #565656;
  padding: 1.2rem 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
`

const StakeAmountTexts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
  width: 100%;
`

const StakeMainAmount = styled.input`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  background: transparent;
  border: none;
  outline: none;
  text-align: right;
  width: 100%;

  &::placeholder {
    color: #6c7284;
  }
`

const StakeSubAmount = styled.div`
  font-size: 0.85rem;
  color: #b0b5c0;
  text-align: right;
`

const StakeTokenInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const StakeTokenLogo = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: transparent;
  border: 1px solid #565656;
`

const StakeTokenName = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
`

const StakeBalanceText = styled.div`
  font-size: 0.85rem;
  color: #b0b5c0;
  margin-bottom: 0.75rem;
`

const StakeSliderWrapper = styled.div`
  margin-bottom: 0.25rem;
`

const StakeSliderTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #4b4f5c;
  overflow: hidden;
`

const StakeSliderFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${({ percent }) => `${percent}%`};
  background: #e9ad21;
`

const StakeSliderPercent = styled.div`
  font-size: 0.85rem;
  color: #ffffff;
  text-align: right;
  margin-bottom: 1rem;
`

const StakePercentButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`

const StakePercentButton = styled.button`
  flex: 1;
  padding: 0.6rem 0;
  border-radius: 8px;
  border: none;
  background-color: #565656;
  color: #e9ad21;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`

const StakeRoiRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 2rem 0 1.5rem;
`

const StakeRoiLabel = styled.span`
  color: #ffffff;
  font-size: 0.9rem;
`

const StakeRoiValue = styled.span`
  color: #ffffff;
  font-size: 1rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const CalculatorIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  color: inherit;

  &:hover {
    opacity: 0.8;
  }

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
  }
`

const StakeConfirmButton = styled(ButtonPrimary)<{ disabled?: boolean }>`
  width: 100%;
  margin-bottom: 0.75rem;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 15px;
  background-color: ${({ disabled }) => (disabled ? '#565656' : '#e9ad21')};
  color: ${({ disabled }) => (disabled ? '#999' : '#000')};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.6 : 0.9)};
  }
`

const StakeGetButton = styled(ButtonSecondary)`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 700;
  border-radius: 15px;
  background-color: #000;
  border-color: #e9ad21;
`

interface StakeModalProps {
  isOpen: boolean
  onDismiss: () => void
  pool: Pool
  stakeAmount: string
  onStakeAmountChange: (value: string) => void
  onConfirm: () => void
  balance: string
  maxStakePerUser?: string
  stakeTokenLogoURL?: string
  stakePrice?: number | null
  isStaking?: boolean
}

export default function StakeModal({
  isOpen,
  onDismiss,
  pool,
  stakeAmount,
  onStakeAmountChange,
  onConfirm,
  balance,
  maxStakePerUser,
  stakeTokenLogoURL,
  stakePrice,
  isStaking = false
}: StakeModalProps) {
  const { t } = useTranslation()
  const [percent, setPercent] = useState(0)
  const [showROICalculator, setShowROICalculator] = useState(false)

  const stakeDecimals = pool.stakeTokenDecimals || 18

  const numericBalance = useMemo(() => {
    const v = parseFloat(balance || '0')
    return Number.isFinite(v) ? v : 0
  }, [balance])

  const formattedBalance = useMemo(() => {
    if (!Number.isFinite(numericBalance)) return '0'
    if (numericBalance === 0) return '0'
    if (numericBalance < 0.0001) return '<0.0001'
    const decimals = Math.min(stakeDecimals, 6)
    const factor = 10 ** decimals
    const floored = Math.floor(numericBalance * factor) / factor
    return floored.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals
    })
  }, [numericBalance, stakeDecimals])

  // 计算 USD 金额
  const usdAmount = useMemo(() => {
    if (!stakeAmount || parseFloat(stakeAmount) <= 0) return '--'
    if (!stakePrice || stakePrice <= 0) return '--'
    const tokenValue = parseFloat(stakeAmount)
    const usdValue = tokenValue * stakePrice
    return usdValue.toFixed(6)
  }, [stakeAmount, stakePrice])

  // 计算 Annual ROI
  const annualROI = useMemo(() => {
    const usdValue = parseFloat(usdAmount)
    if (!usdValue || usdValue <= 0 || !pool.apr || pool.apr <= 0) return '--'
    const roi = usdValue * (pool.apr / 100)
    return roi.toFixed(2)
  }, [usdAmount, pool.apr])

  // 当 stakeAmount 变化时，更新百分比
  useEffect(() => {
    if (!stakeAmount || numericBalance === 0) {
      setPercent(0)
      return
    }
    const amountValue = parseFloat(stakeAmount)
    if (amountValue <= 0 || !Number.isFinite(amountValue)) {
      setPercent(0)
      return
    }
    const calculatedPercent = (amountValue / numericBalance) * 100
    setPercent(Math.min(100, Math.max(0, calculatedPercent)))
  }, [stakeAmount, numericBalance])

  const handlePercentClick = (p: number) => {
    setPercent(p)
    const decimals = Math.min(stakeDecimals, 6)
    const raw = numericBalance * (p / 100)
    const factor = 10 ** decimals
    const floored = Math.floor(raw * factor) / factor
    const amount = floored.toFixed(decimals)
    onStakeAmountChange(parseFloat(amount) === 0 ? '' : Math.max(0, parseFloat(amount)).toString())
  }

  const handleGetToken = () => {
    window.open('https://app.usd1swap.ai/#/swap', '_blank')
  }

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90}>
      <StakeModalContent>
        <StakeHeader>
          <StakeTitle>{t('yieldFarming.stakeInPool')}</StakeTitle>
          <StakeCloseButton onClick={onDismiss} />
        </StakeHeader>

        <Section>
          <StakeHeaderRow>
            <StakeSectionLabel>{t('yieldFarming.staked')}</StakeSectionLabel>
            <StakeTokenInfo>
              <StakeTokenLogo
                style={
                  stakeTokenLogoURL
                    ? {
                        backgroundImage: `url(${stakeTokenLogoURL})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }
                    : undefined
                }
              />
              <StakeTokenName>{pool.stakeToken}</StakeTokenName>
            </StakeTokenInfo>
          </StakeHeaderRow>
          <StakeAmountCard>
            <StakeAmountTexts>
              <StakeMainAmount
                type="text"
                placeholder="0.0"
                value={stakeAmount}
                onChange={e => onStakeAmountChange(e.target.value.replace(/[^0-9.]/g, ''))}
              />
              <StakeSubAmount>{usdAmount} USD1</StakeSubAmount>
            </StakeAmountTexts>
          </StakeAmountCard>

          <StakeBalanceText>
            {t('yieldFarming.balance')}: {formattedBalance} {pool.stakeToken}
            {maxStakePerUser && maxStakePerUser !== '0' ? ` • ${t('yieldFarming.maxUser')}: ${maxStakePerUser}` : ''}
          </StakeBalanceText>

          <StakeSliderWrapper>
            <StakeSliderTrack>
              <StakeSliderFill percent={percent} />
            </StakeSliderTrack>
          </StakeSliderWrapper>
          <StakeSliderPercent>{percent.toFixed(2)}%</StakeSliderPercent>

          <StakePercentButtons>
            <StakePercentButton onClick={() => handlePercentClick(25)}>25%</StakePercentButton>
            <StakePercentButton onClick={() => handlePercentClick(50)}>50%</StakePercentButton>
            <StakePercentButton onClick={() => handlePercentClick(75)}>75%</StakePercentButton>
            <StakePercentButton onClick={() => handlePercentClick(100)}>{t('yieldFarming.max')}</StakePercentButton>
          </StakePercentButtons>

          <StakeRoiRow>
            <StakeRoiLabel>{t('yieldFarming.annualROICurrentRates')}</StakeRoiLabel>
            <StakeRoiValue>
              {annualROI} %
              <CalculatorIconButton
                onClick={e => {
                  e.stopPropagation()
                  setShowROICalculator(true)
                }}
              >
                <img src="/images/calculator.png" alt="calculator" />
              </CalculatorIconButton>
            </StakeRoiValue>
          </StakeRoiRow>

          <StakeConfirmButton onClick={onConfirm} disabled={isStaking}>
            {isStaking ? t('yieldFarming.staking') : t('yieldFarming.confirm')}
          </StakeConfirmButton>
          <StakeGetButton onClick={handleGetToken}>
            {t('yieldFarming.getToken', { token: pool.stakeToken })}
          </StakeGetButton>
        </Section>
      </StakeModalContent>
      <ROICalculatorModal
        isOpen={showROICalculator}
        onDismiss={() => setShowROICalculator(false)}
        apr={pool.apr}
        stakeToken={pool.stakeToken}
        stakePrice={pool.stakePrice}
        rewardToken={pool.rewardToken}
        rewardPrice={pool.rewardPrice}
        totalStaked={pool.totalStaked}
        rewardPerSecond={pool.rewardPerSecond}
        balance={balance}
      />
    </Modal>
  )
}
